// useDeviceHeading — surface the phone's compass heading (degrees, true north)
// so the map can draw a directional cone on the GPS marker, like Google Maps.
//
// Behaviour:
//   - iOS Safari: prefer `webkitCompassHeading` (already calibrated to true
//     north). Needs DeviceOrientationEvent.requestPermission() — call
//     `request()` from a user gesture (button tap). Until granted, heading is
//     null and the cone is hidden.
//   - Android Chrome: use `alpha` from `deviceorientationabsolute` (or fall
//     back to `deviceorientation`). Compensate for screen rotation so heading
//     stays true-north no matter how the user holds the phone.
//   - Desktop / unsupported: heading stays null; map falls back to the plain
//     crosshair.
//
// Heading semantics: 0 = north, 90 = east, 180 = south, 270 = west.
// Updates are throttled to ~10 Hz via ref + setState in handler.
//
// Cleanup: removes listeners on unmount, including the "absolute" variant if
// it was attached.

import { useCallback, useEffect, useRef, useState } from "react";

type WebkitOrientationEvent = DeviceOrientationEvent & {
  webkitCompassHeading?: number;
  webkitCompassAccuracy?: number;
};

type IOSDeviceOrientationEventCtor = typeof DeviceOrientationEvent & {
  requestPermission?: () => Promise<"granted" | "denied">;
};

const THROTTLE_MS = 100;

export interface UseDeviceHeadingResult {
  /** Current heading in degrees [0..360), or null if unknown / unsupported. */
  heading: number | null;
  /** Whether permission has been resolved (iOS). */
  permission: "unknown" | "granted" | "denied" | "unsupported";
  /** Call from a user gesture to prompt for permission (iOS) and start
   *  listening. Idempotent. On Android & desktop, just starts listening. */
  request: () => Promise<void>;
}

export function useDeviceHeading(): UseDeviceHeadingResult {
  const [heading, setHeading] = useState<number | null>(null);
  const [permission, setPermission] = useState<
    "unknown" | "granted" | "denied" | "unsupported"
  >(() => {
    if (typeof window === "undefined") return "unsupported";
    if (typeof DeviceOrientationEvent === "undefined") return "unsupported";
    return "unknown";
  });
  const lastUpdateRef = useRef<number>(0);
  const startedRef = useRef<boolean>(false);

  const handleOrientation = useCallback((e: DeviceOrientationEvent) => {
    const now = Date.now();
    if (now - lastUpdateRef.current < THROTTLE_MS) return;

    const webkit = (e as WebkitOrientationEvent).webkitCompassHeading;
    let h: number | null = null;
    if (typeof webkit === "number" && Number.isFinite(webkit)) {
      // iOS: already calibrated to true north, clockwise.
      h = webkit;
    } else if (typeof e.alpha === "number" && Number.isFinite(e.alpha)) {
      // Android: alpha is counter-clockwise from north (when absolute=true) or
      // relative to device's last known orientation (when absolute=false).
      // Compensate for screen rotation so a sideways phone still points north.
      const screenAngle =
        typeof window !== "undefined" && window.screen?.orientation?.angle
          ? window.screen.orientation.angle
          : 0;
      h = (360 - e.alpha + screenAngle) % 360;
    }
    if (h !== null) {
      lastUpdateRef.current = now;
      setHeading(h);
    }
  }, []);

  const start = useCallback(() => {
    if (startedRef.current) return;
    if (typeof window === "undefined") return;
    startedRef.current = true;
    // Prefer the absolute variant when available — it's tied to true north
    // independent of where the phone happened to be facing at app start.
    if ("ondeviceorientationabsolute" in window) {
      window.addEventListener(
        "deviceorientationabsolute" as "deviceorientation",
        handleOrientation,
        { passive: true },
      );
    }
    window.addEventListener("deviceorientation", handleOrientation, {
      passive: true,
    });
  }, [handleOrientation]);

  const request = useCallback(async () => {
    if (typeof window === "undefined") return;
    const Ctor = (window as unknown as { DeviceOrientationEvent?: IOSDeviceOrientationEventCtor }).DeviceOrientationEvent;
    if (!Ctor) return;
    if (typeof Ctor.requestPermission === "function") {
      // iOS 13+: must be invoked from a user gesture.
      try {
        const result = await Ctor.requestPermission();
        setPermission(result);
        if (result === "granted") start();
      } catch {
        setPermission("denied");
      }
    } else {
      // Android / non-iOS: no permission gate. Just start listening.
      setPermission("granted");
      start();
    }
  }, [start]);

  // On non-iOS platforms, start listening eagerly (no permission needed).
  // iOS still waits for `request()` from a user gesture.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const Ctor = (window as unknown as { DeviceOrientationEvent?: IOSDeviceOrientationEventCtor }).DeviceOrientationEvent;
    if (!Ctor) return;
    if (typeof Ctor.requestPermission !== "function") {
      start();
    }
    return () => {
      if (!startedRef.current) return;
      window.removeEventListener("deviceorientation", handleOrientation);
      if ("ondeviceorientationabsolute" in window) {
        window.removeEventListener(
          "deviceorientationabsolute" as "deviceorientation",
          handleOrientation,
        );
      }
      startedRef.current = false;
    };
  }, [start, handleOrientation]);

  return { heading, permission, request };
}
